import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { apiFetch } from "@/utils/api";
import type { AuthUser, TenantInfo } from "@/utils/permissions";

interface Membership {
  tenantId: string;
  tenantName: string;
  slug: string;
  roleLabel: string;
  isOwner: boolean;
  subscriptionStatus: string;
  isCurrent?: boolean;
}

interface AuthContextValue {
  token: string | null;
  user: AuthUser | null;
  tenant: TenantInfo | null;
  memberships: Membership[];
  loading: boolean;
  login: (
    email: string,
    password: string,
  ) => Promise<{
    success: boolean;
    error?: string;
    requiresTenantSelection?: boolean;
    memberships?: Membership[];
  }>;
  selectTenant: (email: string, password: string, tenantId: string) => Promise<{ success: boolean; error?: string }>;
  switchTenant: (tenantId: string) => Promise<{ success: boolean; error?: string }>;
  enterSupport: (tenantId: string) => Promise<{ success: boolean; error?: string }>;
  exitSupport: () => Promise<{ success: boolean; error?: string }>;
  setSession: (token: string, user: AuthUser, tenant: TenantInfo) => void;
  logout: () => void;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function persistSession(token: string, user: AuthUser, tenant: TenantInfo | null) {
  localStorage.setItem("authToken", token);
  localStorage.setItem("authUser", JSON.stringify(user));
  if (tenant) localStorage.setItem("authTenant", JSON.stringify(tenant));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem("authToken"));
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const raw = localStorage.getItem("authUser");
      return raw ? (JSON.parse(raw) as AuthUser) : null;
    } catch {
      return null;
    }
  });
  const [tenant, setTenant] = useState<TenantInfo | null>(() => {
    try {
      const raw = localStorage.getItem("authTenant");
      return raw ? (JSON.parse(raw) as TenantInfo) : null;
    } catch {
      return null;
    }
  });
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [loading, setLoading] = useState(Boolean(token));

  const refreshMe = async () => {
    if (!token) return;
    try {
      const data = await apiFetch<{ user: AuthUser; tenant: TenantInfo }>("/auth/me", { token });
      setUser(data.user);
      setTenant(data.tenant);
      localStorage.setItem("authUser", JSON.stringify(data.user));
      localStorage.setItem("authTenant", JSON.stringify(data.tenant));
      const mem = await apiFetch<{ memberships: Membership[] }>("/auth/memberships", { token });
      setMemberships(mem.memberships);
    } catch {
      setToken(null);
      setUser(null);
      setTenant(null);
      localStorage.removeItem("authToken");
      localStorage.removeItem("authUser");
      localStorage.removeItem("authTenant");
    }
  };

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    (async () => {
      await refreshMe();
      setLoading(false);
    })();
  }, [token]);

  const setSession = (t: string, u: AuthUser, ten: TenantInfo) => {
    setToken(t);
    setUser(u);
    setTenant(ten);
    persistSession(t, u, ten);
  };

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const data = await apiFetch<{
        requiresTenantSelection?: boolean;
        memberships?: Membership[];
        token?: string;
        user?: AuthUser;
        tenant?: TenantInfo;
      }>("/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
        token: null,
      });

      if (data.requiresTenantSelection && data.memberships) {
        return { success: true, requiresTenantSelection: true, memberships: data.memberships };
      }

      if (data.token && data.user && data.tenant) {
        setSession(data.token, data.user, data.tenant);
        return { success: true };
      }
      return { success: false, error: "Unexpected login response" };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Login failed",
      };
    } finally {
      setLoading(false);
    }
  };

  const selectTenant = async (email: string, password: string, tenantId: string) => {
    try {
      const data = await apiFetch<{ token: string; user: AuthUser; tenant: TenantInfo }>(
        "/auth/select-tenant",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password, tenantId }),
          token: null,
        },
      );
      setSession(data.token, data.user, data.tenant);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to select organization",
      };
    }
  };

  const switchTenant = async (tenantId: string) => {
    if (!token) return { success: false, error: "Not logged in" };
    if (user?.supportMode) {
      return { success: false, error: "Exit support mode before switching organizations." };
    }
    try {
      const data = await apiFetch<{ token: string; user: AuthUser; tenant: TenantInfo }>(
        "/auth/switch-tenant",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tenantId }),
          token,
        },
      );
      setSession(data.token, data.user, data.tenant);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to switch organization",
      };
    }
  };

  const enterSupport = async (tenantId: string) => {
    if (!token) return { success: false, error: "Not logged in" };
    try {
      const data = await apiFetch<{ token: string; user: AuthUser; tenant: TenantInfo }>(
        `/platform/tenants/${tenantId}/support-enter`,
        { method: "POST", token },
      );
      setSession(data.token, data.user, data.tenant);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to enter support mode",
      };
    }
  };

  const exitSupport = async () => {
    if (!token) return { success: false, error: "Not logged in" };
    try {
      const data = await apiFetch<{ token: string; user: AuthUser; tenant: TenantInfo }>(
        "/platform/support-exit",
        { method: "POST", token },
      );
      setSession(data.token, data.user, data.tenant);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to exit support mode",
      };
    }
  };

  const logout = () => {
    const t = token;
    setToken(null);
    setUser(null);
    setTenant(null);
    setMemberships([]);
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUser");
    localStorage.removeItem("authTenant");
    if (t) {
      apiFetch("/auth/logout", { method: "POST", token: t }).catch(() => {});
    }
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        tenant,
        memberships,
        loading,
        login,
        selectTenant,
        switchTenant,
        enterSupport,
        exitSupport,
        setSession,
        logout,
        refreshMe,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
